/**
 * Build a single grid-template-columns track value from a column's
 * declared `width` and an optional measured pixel value.
 *
 * Rule:
 *
 *   - Bare intrinsic keyword (`max-content` / `min-content` / `auto`)
 *     or no declared width → lock to `${measured}px` when available.
 *     These are the keywords CSS Grid re-resolves dynamically as cells
 *     mount, the source of column-width flicker.
 *
 *   - `minmax(MIN, MAX)` → augment MIN to `max(MIN, measured)` and
 *     leave MAX unchanged.  The developer chose `minmax(…)` for a
 *     reason — that's a deliberate opt-in to flexibility on the MAX
 *     side, which we preserve.  The augmented MIN reserves the
 *     measured width as a floor; the MAX is free to re-resolve.
 *
 *   - `fr` units absorb leftover space so the grid auto-fits its host.
 *     Preserved as declared.
 *
 *   - Fixed declarations (em, px, %, fixed-MAX minmax) specify exact
 *     sizes the developer chose.  Preserved as declared.
 *
 * Pure: no DOM, no React, no state.  Inputs map deterministically to
 * one output string.
 */

const INTRINSIC_KEYWORD = /^(max-content|min-content|auto)$/i;
// Strict `minmax(MIN, MAX)` matcher: rejects top-level commas or parens
// inside MIN/MAX so three-arg pseudo-minmax garbage doesn't get
// silently rewritten, and so we never try to parse a `minmax(max(…), …)`
// (the developer wrote complex CSS for a reason; leave it alone).
const MINMAX = /^minmax\(\s*([^,()]+?)\s*,\s*([^,()]+?)\s*\)$/i;

export function buildGridColumnTrack(
    declared: string | number | undefined | null,
    measured: number | undefined,
): string {
    if (declared != null) {
        // Unitless numeric width: append `px` so the value is valid CSS.
        // Without this, `width: 120` would emit `"120"` which CSS Grid
        // rejects as a track-size and the column falls back to default.
        if (typeof declared === 'number') {
            return `${declared}px`;
        }
        const w = declared.trim();
        if (INTRINSIC_KEYWORD.test(w)) {
            return measured != null ? `${measured}px` : w;
        }
        const mm = w.match(MINMAX);
        if (mm) {
            if (measured == null) return w;
            const min = mm[1].trim();
            // CSS `max()` requires `<length-percentage>` arguments —
            // intrinsic keywords (min-content / max-content / auto)
            // are NOT lengths.  Wrapping them in `max(intrinsic, Npx)`
            // produces invalid CSS that browsers silently drop.  When
            // the MIN is intrinsic, the developer chose shrinkable
            // sizing on purpose; leave the declaration untouched
            // rather than overriding with a measured floor.
            if (INTRINSIC_KEYWORD.test(min)) {
                return w;
            }
            return `minmax(max(${min}, ${measured}px), ${mm[2]})`;
        }
        return w;
    }
    if (measured != null) return `${measured}px`;
    return 'max-content';
}
